import { MinusIcon, PlusIcon } from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  IconButton,
  RadioCards,
} from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSliderInput } from 'components/common/form/slider';
import { t } from 'i18next';
import {
  Orientation,
  ORIENTATION_SEAM_ALT_AZ,
  SEAM_ORIENTATIONS,
} from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { FC, useState } from 'react';
import { usePitchDesignStore } from 'stores/use-pitch-design-store';

const COMPONENT_NAME = 'PDSeamOrientation';

const LIMIT_LATITUDE_DEG = 90;
const LIMIT_LONGITUDE_DEG = 180;

export const SeamOrientation: FC = () => {
  const showCurrent = usePitchDesignStore((s) => s.showCurrent);
  const inputs = usePitchDesignStore((s) => s.inputs);
  const refBall = usePitchDesignStore((s) => s.refBall);
  const { seams } = inputs;

  const [activeOrientation, setActiveOrientation] = useState(
    refBall.orientation ?? Orientation.FF4
  );

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <Flex direction="column" gap={RADIX.FLEX.GAP.FORM}>
        <Heading size={RADIX.HEADING.SIZE.SM}>
          {t('pd.seam-orientation')}
        </Heading>

        <RadioCards.Root
          size="1"
          gap={RADIX.FLEX.GAP.SM}
          defaultValue={activeOrientation}
          onValueChange={(value) => {
            // change active segment
            setActiveOrientation(value as Orientation);

            const definition = ORIENTATION_SEAM_ALT_AZ[value];
            if (!definition) {
              return;
            }

            // update seams to match definition
            usePitchDesignStore.getState().mergeInputs({ seams: definition });
          }}
        >
          {SEAM_ORIENTATIONS.map((o, i) => (
            <RadioCards.Item key={i} value={o.value}>
              {t(o.label)}
              {showCurrent && refBall.orientation === o.value ? '*' : ''}
            </RadioCards.Item>
          ))}
        </RadioCards.Root>

        {activeOrientation === Orientation.CUS && (
          <Grid columns="2" gap={RADIX.FLEX.GAP.SM}>
            <Flex gap={RADIX.FLEX.GAP.SM} justify="center">
              <Box>
                <IconButton
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  disabled={seams.latitude_deg <= -LIMIT_LATITUDE_DEG}
                  onClick={() => {
                    const next = seams.latitude_deg - 1;
                    usePitchDesignStore
                      .getState()
                      .mergeInputs({ seams: { ...seams, latitude_deg: next } });
                  }}
                >
                  <MinusIcon />
                </IconButton>
              </Box>
              <Box flexGrow="1">
                <Button
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  className="btn-block"
                  onClick={() => {
                    const next = showCurrent ? refBall.latitude_deg : 0;
                    usePitchDesignStore
                      .getState()
                      .mergeInputs({ seams: { ...seams, latitude_deg: next } });
                  }}
                >
                  {t('pd.latitude')}: {seams.latitude_deg.toFixed(0)}
                </Button>
              </Box>
              <Box>
                <IconButton
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  disabled={seams.latitude_deg >= LIMIT_LATITUDE_DEG}
                  onClick={() => {
                    const next = seams.latitude_deg + 1;
                    usePitchDesignStore
                      .getState()
                      .mergeInputs({ seams: { ...seams, latitude_deg: next } });
                  }}
                >
                  <PlusIcon />
                </IconButton>
              </Box>
            </Flex>

            <Flex gap={RADIX.FLEX.GAP.SM} justify="center">
              <Box>
                <IconButton
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  disabled={seams.longitude_deg <= -LIMIT_LONGITUDE_DEG}
                  onClick={() => {
                    const next = seams.longitude_deg - 1;
                    usePitchDesignStore.getState().mergeInputs({
                      seams: { ...seams, longitude_deg: next },
                    });
                  }}
                >
                  <MinusIcon />
                </IconButton>
              </Box>
              <Box flexGrow="1">
                <Button
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  className="btn-block"
                  onClick={() => {
                    const next = showCurrent ? refBall.longitude_deg : 0;
                    usePitchDesignStore.getState().mergeInputs({
                      seams: { ...seams, longitude_deg: next },
                    });
                  }}
                >
                  {t('pd.longitude')}: {seams.longitude_deg.toFixed(0)}
                </Button>
              </Box>
              <Box>
                <IconButton
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  disabled={seams.longitude_deg >= LIMIT_LONGITUDE_DEG}
                  onClick={() => {
                    const next = seams.longitude_deg + 1;
                    usePitchDesignStore.getState().mergeInputs({
                      seams: { ...seams, longitude_deg: next },
                    });
                  }}
                >
                  <PlusIcon />
                </IconButton>
              </Box>
            </Flex>

            <Box p="2">
              <CommonSliderInput
                id="PDSeamOrientationLatitude"
                min={-LIMIT_LATITUDE_DEG}
                max={LIMIT_LATITUDE_DEG}
                step={1}
                value={seams.latitude_deg}
                onChange={(next) =>
                  usePitchDesignStore.getState().mergeInputs({
                    seams: { ...seams, latitude_deg: next },
                  })
                }
                hint_md={
                  showCurrent
                    ? t('pd.current-x', {
                        x: refBall.latitude_deg.toFixed(0),
                      }).toString()
                    : undefined
                }
              />
            </Box>
            <Box p="2">
              <CommonSliderInput
                id="PDSeamOrientationLongitude"
                min={-LIMIT_LONGITUDE_DEG}
                max={LIMIT_LONGITUDE_DEG}
                step={1}
                value={seams.longitude_deg}
                onChange={(next) =>
                  usePitchDesignStore.getState().mergeInputs({
                    seams: { ...seams, longitude_deg: next },
                  })
                }
                hint_md={
                  showCurrent
                    ? t('pd.current-x', {
                        x: refBall.longitude_deg.toFixed(0),
                      }).toString()
                    : undefined
                }
              />
            </Box>
          </Grid>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
